import { env } from '@/env';
import { getIsMobilePlatform } from '@/utils/device';
import { isWregDesktopViewport } from '@/utils/viewport';

declare global {
  interface Window {
    Forethought: (key: string, value: string, payload?: Record<string, string>) => void;
  }
}

const CHATBOT_SCRIPT_ID = 'forethought-widget-embed-script';
const CHATBOT_ORIGIN = 'https://solve-widget.forethought.ai';
// Forethought sizes the launcher button from the SVG's own width/height.
const CHATBOT_BUTTON_SIZE_PX = 56;
const CHATBOT_WIDGET_BUTTON_IMAGE =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" ' +
      `width="${CHATBOT_BUTTON_SIZE_PX}" height="${CHATBOT_BUTTON_SIZE_PX}">` +
      '<circle cx="12" cy="12" r="12" fill="#04747A"/>' +
      '<g fill="#ffffff" transform="translate(4,4) scale(0.667)">' +
      '<path opacity=".7" d="M19,3H5C3.343,3,2,4.343,2,6v13v0.828c0,1.746,1.974,2.762,3.395,1.747l2.822-2.016C8.726,19.195,9.336,19,9.961,19H19c1.657,0,3-1.343,3-3V6C22,4.343,20.657,3,19,3z"/>' +
      '<path d="M15,10H9c-0.553,0-1-0.448-1-1s0.447-1,1-1h6c0.553,0,1,0.448,1,1S15.553,10,15,10z"/>' +
      '<path d="M13,14H9c-0.553,0-1-0.448-1-1s0.447-1,1-1h4c0.553,0,1,0.448,1,1S13.553,14,13,14z"/>' +
      '</g>' +
      '</svg>'
  );
const CHATBOT_WORKFLOW_TAG = 'happy_hearts';

const MOBILE_OFFSET_Y = '24px';
const DESKTOP_OFFSET_Y = '90px';
// 0px puts the round button flush against the viewport edge.
const OFFSET_X = '16px';
// Forethought draws the button's bottom edge this far below offset-y.
const BUTTON_DROP_PX = 8;
// Its transparent iframe reaches this far below the button and swallows taps.
const FRAME_BELOW_BUTTON_PX = 20;

type ChatBotConfig = Map<string, string>;

// Read the viewport too: a mobile UA past the desktop breakpoint (iPad, landscape) gets the
// desktop layout, where useChatLauncherDock is off and the CTA sits where 24px would put the launcher.
const defaultOffsetY = () =>
  getIsMobilePlatform() && !isWregDesktopViewport() ? MOBILE_OFFSET_Y : DESKTOP_OFFSET_Y;

const defaultAttributes = new Map<string, string>([
  ['src', `${CHATBOT_ORIGIN}/embed.js`],
  ['id', CHATBOT_SCRIPT_ID],
  ['data-api-key', env.FORETHOUGHT_API_KEY],
  ['data-ft-workflow-tag', CHATBOT_WORKFLOW_TAG],
  ['config-ft-widget-button-image', CHATBOT_WIDGET_BUTTON_IMAGE],
  ['emit-tracking-events', 'true'],
  ['offset-x', OFFSET_X],
  ['offset-y', defaultOffsetY()],
  ['position-y', 'bottom'],
  ['position-x', 'right'],
  ['data-ft-embed-script-language', 'en'],
  // ['hidden', 'true'], // TODO future - Uncomment this line to hide chatbot by default
]);

const isAttributesEqual = (existingScriptTag: HTMLScriptElement, newConfig: ChatBotConfig) => {
  for (const [key, value] of newConfig) {
    if (existingScriptTag.getAttribute(key) !== value) {
      return false;
    }
  }
  return true;
};

// embed.js may never define window.Forethought (ad blocker, CSP, network), so stop polling after this.
const FORETHOUGHT_READY_POLLS = 30;

let readyPollPointer = 0;
let readyPollsLeft = FORETHOUGHT_READY_POLLS;
const pendingCalls = new Set<() => void>();
let existingScriptTag: HTMLScriptElement | null = null;
let isWidgetVisible = true;
let isLauncherSuppressed = false;
let dockedOffsetY = defaultOffsetY();
let appliedOffsetY = dockedOffsetY;
let isChatOpen = false;

const isForethoughtReady = () => typeof window.Forethought === 'function';

const pollForethoughtReady = () => {
  readyPollPointer = 0;
  if (isForethoughtReady()) {
    pendingCalls.forEach((call) => call());
    pendingCalls.clear();
  } else if (--readyPollsLeft > 0) {
    readyPollPointer = window.setTimeout(pollForethoughtReady, 1000);
  } else {
    pendingCalls.clear();
  }
};

// Each call reads the current state when it runs, so a queued call always sends the latest value.
const whenForethoughtReady = (call: () => void) => {
  if (isForethoughtReady()) {
    call();
    return;
  }
  pendingCalls.add(call);
  if (!readyPollPointer) {
    readyPollPointer = window.setTimeout(pollForethoughtReady, 1000);
  }
};

const sendVisibility = () =>
  window.Forethought('widget', isWidgetVisible && !isLauncherSuppressed ? 'show' : 'hide');

const sendOffset = () =>
  window.Forethought('widget', 'updatePositionParams', { 'offset-y': appliedOffsetY });

const applyVisibility = () => whenForethoughtReady(sendVisibility);

// Forethought sizes the open panel from offset-y on wide screens; the docked offset shrinks it.
const applyOffset = () => {
  const next = isChatOpen ? defaultOffsetY() : dockedOffsetY;
  if (next === appliedOffsetY) {
    return;
  }
  appliedOffsetY = next;
  // The attribute only helps if embed.js has not read it yet, so also send it once the API exists.
  existingScriptTag?.setAttribute('offset-y', appliedOffsetY);
  whenForethoughtReady(sendOffset);
};

const onWidgetMessage = (event: MessageEvent) => {
  const name = event.origin === CHATBOT_ORIGIN ? event.data?.event : undefined;
  if (name === 'forethoughtWidgetOpened' || name === 'forethoughtWidgetClosed') {
    isChatOpen = name === 'forethoughtWidgetOpened';
    applyOffset();
  }
};

window.addEventListener('message', onWidgetMessage);

export const chatBotManager = {
  init: (config: Map<string, string> = new Map()) => {
    const configWith = (offsetY: string) =>
      new Map([...defaultAttributes, ...config, ['offset-y', offsetY]]);
    if (existingScriptTag && isAttributesEqual(existingScriptTag, configWith(appliedOffsetY))) {
      return;
    }
    // A rebuilt widget starts closed and never sends forethoughtWidgetClosed.
    isChatOpen = false;
    appliedOffsetY = dockedOffsetY;
    readyPollsLeft = FORETHOUGHT_READY_POLLS;
    const combinedConfig = configWith(appliedOffsetY);

    existingScriptTag?.remove();
    if (document.querySelector(`#${CHATBOT_SCRIPT_ID}`)) {
      document.querySelector(`#${CHATBOT_SCRIPT_ID}`)?.remove();
    }

    const script = document.createElement('script');
    for (const [key, value] of combinedConfig) {
      script.setAttribute(key, value);
    }
    existingScriptTag = script;

    document.body.prepend(script);
  },
  open: () => {
    isLauncherSuppressed = false;
    chatBotManager.setWidgetVisibility(true);
    if (window.Forethought) {
      window.Forethought('widget', 'open');
    }
  },
  close: () => {
    if (window.Forethought) {
      window.Forethought('widget', 'close');
    }
  },
  placeLauncherAbove: (topFromBottomPx: number | null) => {
    dockedOffsetY =
      topFromBottomPx === null
        ? defaultOffsetY()
        : `${Math.round(topFromBottomPx + FRAME_BELOW_BUTTON_PX + BUTTON_DROP_PX)}px`;
    applyOffset();
  },
  setWidgetVisibility: (isVisible: boolean) => {
    isWidgetVisible = isVisible;
    applyVisibility();
  },
  suppressLauncher: (isSuppressed: boolean) => {
    if (isSuppressed === isLauncherSuppressed) {
      return;
    }
    isLauncherSuppressed = isSuppressed;
    applyVisibility();
  },
};
